import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router';
import { Tab, Tabs, Button } from '@mui/material';

import { useAssets } from '@labradorsports/assets';
import { ButtonsBar, Flyover, Panel } from '@labradorsports/components';
import { CommunityPlaybookSections, PlaybookTabs } from '@labradorsports/constants';
import { useSite } from '@labradorsports/utils';

import { Footer } from '../../main/index.js';
import { PlaybookHeader, PlayList } from '../../playbook/index.js';
import { ShareMyPlayCommunity } from '../../playbook/sharing/index.js';
import { useDispatcher } from '../../shared/hooks/index.js';
import { useNavigateWithQuery } from '../../shared/providers/index.js';
import { playbookActions, useGetCommunityPlayQuery } from '../../store/index.js';
import { RouterSelectors, Selectors } from '../../store/state.js';
import CommunityHome from '../community-home/community-home.js';
import CommunityPlay from '../community-play/community-play.js';
import CommunityProfile from '../community-profile/community-profile.js';
import CommunitySearch from '../community-search/community-search.js';
import CommunitySearchPage from '../community-search-page/community-search-page.js';
import CommunitySectionPage from '../community-section-page/community-section-page.js';

const CommunityTabs = {
    COMMUNITY: '',
    MYPROFILE: '/profile',
    PLAY: '/play',
};

const CommunityPage: FC = () => {
    const { PlaybookEmpty } = useAssets();
    const { Config } = useSite();
    const user = useSelector(Selectors.user);
    const setShareConfig = useDispatcher(playbookActions.SetSharing);
    const shareConfig = useSelector(Selectors.shareConfig);
    const communityPlayId = useSelector(RouterSelectors.communityPlayId);
    const { data: openPlay } = useGetCommunityPlayQuery({ id: communityPlayId }, { skip: !communityPlayId });
    const [addingPlay, setAddingPlay] = useState(false);

    const navigate = useNavigateWithQuery();
    const location = useLocation();
    const subpath = location.pathname.replace('/community', '');

    const tabChange = (evt, value) => {
        navigate(`/community${value}`);
    };

    const addPlay = () => setAddingPlay(true);

    const cancelAddPlay = () => {
        setAddingPlay(false);
        setShareConfig();
    };

    const sharePlay = (playId: string) => {
        setShareConfig({
            itemIds: [playId],
        });
    };

    return (
        <div className="community-page">
            <div className="community-tabs panel-tabs">
                <Tabs value={subpath} onChange={tabChange}>
                    <Tab value={CommunityTabs.COMMUNITY} label="Community Playbook" />
                    {user ? <Tab value={CommunityTabs.MYPROFILE} label="My Profile" /> : null}
                    {subpath === CommunityTabs.PLAY ? <Tab value={CommunityTabs.PLAY} label={openPlay?.name} /> : null}
                </Tabs>
                <CommunitySearch position="left" />
            </div>
            <div className="community-content page-content-panel">
                <Flyover in={addingPlay}>
                    {shareConfig ? (
                        <ShareMyPlayCommunity onCancel={cancelAddPlay} />
                    ) : (
                        <Panel
                            noPadding
                            header={<PlaybookHeader title="My Playbook" />}
                            content={
                                <>
                                    <div className="p-2 pb-0">Select the play you would like to add:</div>
                                    <PlayList loadPlay={sharePlay} playbookType={PlaybookTabs.MY} readOnly />
                                </>
                            }
                            footer={
                                <ButtonsBar>
                                    <Button className="text-end" onClick={cancelAddPlay}>
                                        Cancel
                                    </Button>
                                </ButtonsBar>
                            }
                        />
                    )}
                </Flyover>
                <Routes>
                    <Route path="/" element={<CommunityHome addPlay={addPlay} />} />
                    <Route
                        path="/latest"
                        element={<CommunitySectionPage addPlay={addPlay} section={CommunityPlaybookSections.LATEST} />}
                    />
                    <Route
                        path="/popular"
                        element={<CommunitySectionPage addPlay={addPlay} section={CommunityPlaybookSections.POPULAR} />}
                    />
                    <Route
                        path="/trending"
                        element={
                            <CommunitySectionPage addPlay={addPlay} section={CommunityPlaybookSections.TRENDING} />
                        }
                    />
                    <Route path="/play" element={<CommunityPlay />} />
                    <Route path="/profile" element={<CommunityProfile addPlay={addPlay} />} />
                    <Route path="/search" element={<CommunitySearchPage />} />
                </Routes>
            </div>
            {user ? null : (
                <div className="community-banner page-content-panel d-sm-flex">
                    <img src={PlaybookEmpty} alt="" />
                    <div className="flex-grow-1">
                        <h4>Playbook Designer and Team Management</h4>
                        <h6>About {Config.SiteConfig.SiteName}</h6>
                        <p className="mb-5">
                            {Config.SiteConfig.SiteName} is {Config.SiteConfig.Sport}&apos;s top playbook design tool.
                            It helps coaches save time by sharing team concepts before practice so that your team shows
                            up for practice ready to play. The full suite of team management tools like the message
                            board, team schedule, practice planning, and scouting reports will help develop your
                            team&apos;s chemistry and accountability.
                        </p>
                        <p>Create a free account to start building plays today!</p>
                        <div>
                            <Button href="/login" className="me-2">
                                Log In
                            </Button>
                            <Button href="/signup" color="secondary" variant="contained">
                                Sign Up
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            <Footer />
        </div>
    );
};

export default CommunityPage;
